import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { 
  Clock, 
  Zap, 
  TrendingUp,
  Target,
  ArrowUpRight,
  BarChart3,
  Mail,
  Info
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { API } from "../../App";
import { apiFetch } from "../../lib/apiFetch";

const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await apiFetch(`${API}/analytics/performance`);
      if (response.ok) {
        setAnalytics(await response.json());
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const COLORS = ['#EF4444', '#F97316', '#8B5CF6', '#3B82F6'];

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-slate-200 dark:bg-slate-800 rounded"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-200 dark:bg-slate-800 rounded-xl"></div>
            ))}
          </div>
          <div className="h-80 bg-slate-200 dark:bg-slate-800 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const isEmpty = !analytics || (
    (analytics.emails_processed || 0) === 0 &&
    (analytics.ai_actions_total || 0) === 0
  );

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
          Analytiques
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
          Mesurez l'impact d'ALIA sur votre productivité.
        </p>
      </div>

      {isEmpty && (
        <Card className="glass-card border border-blue-500/20 bg-blue-500/5" data-testid="analytics-empty">
          <CardContent className="p-5 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700 dark:text-slate-200">
              <p className="font-medium mb-1">Pas encore de données</p>
              <p className="text-slate-500 dark:text-slate-400">
                Connectez votre boîte Outlook et chargez vos emails depuis l'onglet
                <strong> Boîte de réception </strong>
                pour commencer à voir vos analytiques personnelles.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card" data-testid="time-saved-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-500" />
              </div>
              <div className="flex items-center gap-1 text-sm font-medium text-green-500">
                <ArrowUpRight className="w-4 h-4" />
                +35%
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                {analytics?.time_saved_total || 0}h
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Temps économisé
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="actions-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-green-500" />
              </div>
              <div className="flex items-center gap-1 text-sm font-medium text-green-500">
                <ArrowUpRight className="w-4 h-4" />
                +28%
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                {analytics?.ai_actions_total?.toLocaleString() || 0}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Actions IA totales
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="response-time-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-500" />
              </div>
              <div className="flex items-center gap-1 text-sm font-medium text-green-500">
                <ArrowUpRight className="w-4 h-4" />
                {Math.abs(analytics?.response_time_improvement || 0)}%
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                {analytics?.response_time_avg || 0}h
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Temps de réponse moyen
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="efficiency-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-purple-500" />
              </div>
              <Badge className="bg-green-500/20 text-green-500 border-0">
                Excellent
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                {analytics?.efficiency_score || 0}%
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Score d'efficacité
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Activity Chart */}
        <Card className="glass-card" data-testid="weekly-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit' }}>
              <BarChart3 className="w-5 h-5 text-blue-500" />
              Activité hebdomadaire
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics?.weekly_stats || []}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
                  <XAxis 
                    dataKey="day" 
                    className="text-slate-500"
                    tick={{ fill: 'currentColor' }}
                  />
                  <YAxis 
                    className="text-slate-500"
                    tick={{ fill: 'currentColor' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(15, 23, 42, 0.9)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Bar dataKey="emails" fill="#3B82F6" name="Emails" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actions" fill="#10B981" name="Actions IA" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Category Distribution */}
        <Card className="glass-card" data-testid="category-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit' }}>
              <Mail className="w-5 h-5 text-purple-500" />
              Répartition par catégorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics?.category_distribution || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="category"
                  >
                    {(analytics?.category_distribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(15, 23, 42, 0.9)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value, name) => [`${value} emails`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {(analytics?.category_distribution || []).map((item, index) => (
                <div key={item.category} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {item.category} ({item.percentage}%)
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Emails Processed */}
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 uppercase tracking-wide">Emails traités ce mois</p>
              <p className="text-4xl font-bold text-slate-900 dark:text-white mt-2" style={{ fontFamily: 'Outfit' }}>
                {analytics?.emails_processed || 0}
              </p>
            </div>
            <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center">
              <Mail className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPage;
